import { Alert, Box, Card, CardContent, Grid2, Stack, TextField, Button, Typography, useTheme } from "@mui/material";
import { FormEvent, useState } from "react";
import PageTitle from "../components/PageTitle";
import { brand } from "../../shared/util/branding";

const discordInviteUrl = import.meta.env.VITE_DISCORD_INVITE_URL;
const formspreeEndpoint = import.meta.env.VITE_FORMSPREE_ENDPOINT;

type FormStatus = "idle" | "submitting" | "success" | "error";

export default function Contact() {
  const theme = useTheme();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<FormStatus>("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formspreeEndpoint) {
      setStatus("error");
      setErrorMessage("Contact form is not configured. Set VITE_FORMSPREE_ENDPOINT.");
      return;
    }
    setStatus("submitting");
    setErrorMessage("");
    try {
      const res = await fetch(formspreeEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name,
          email,
          message,
          _subject: `${brand.name} contact form: ${name}`,
        }),
      });
      if (!res.ok) {
        const data: { errors?: { message?: string }[] } = await res.json().catch(() => ({}));
        throw new Error(data.errors?.[0]?.message ?? `Request failed (${res.status})`);
      }
      setStatus("success");
      setName("");
      setEmail("");
      setMessage("");
    } catch (err) {
      setStatus("error");
      setErrorMessage(err instanceof Error ? err.message : "Failed to send message");
    }
  };

  return (
    <Box>
      <PageTitle
        title="Contact"
        subTitle={`Get in touch with the ${brand.name} team`}
      />

      <Grid2 container spacing={4}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Card
            sx={{
              background: theme.palette.background.paper,
              height: "100%",
            }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Send us a message
              </Typography>
              <Stack component="form" onSubmit={handleSubmit} spacing={3}>
                <TextField
                  label="Name"
                  variant="outlined"
                  fullWidth
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={status === "submitting"}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "rgba(255, 255, 255, 0.2)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(255, 255, 255, 0.3)",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: theme.palette.text.secondary,
                    },
                  }}
                />
                <TextField
                  label="Email"
                  variant="outlined"
                  fullWidth
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={status === "submitting"}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "rgba(255, 255, 255, 0.2)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(255, 255, 255, 0.3)",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: theme.palette.text.secondary,
                    },
                  }}
                />
                <TextField
                  label="Message"
                  variant="outlined"
                  fullWidth
                  required
                  multiline
                  rows={5}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  disabled={status === "submitting"}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "rgba(255, 255, 255, 0.2)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(255, 255, 255, 0.3)",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: theme.palette.text.secondary,
                    },
                  }}
                />
                {status === "success" && (
                  <Alert severity="success">Thanks! Your message has been sent.</Alert>
                )}
                {status === "error" && (
                  <Alert severity="error">{errorMessage}</Alert>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={status === "submitting"}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    fontWeight: 600,
                    py: 1.5,
                  }}
                >
                  {status === "submitting" ? "Sending..." : "Send Message"}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <Stack spacing={3}>
            {discordInviteUrl && (
              <Card sx={{ background: theme.palette.background.paper }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Community
                  </Typography>
                  <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                    Join our Discord server to chat with other players, get help,
                    and share your games.
                  </Typography>
                  <Button
                    variant="contained"
                    component="a"
                    href={discordInviteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      background: "#5865F2",
                      "&:hover": { background: "#4752C4" },
                    }}
                  >
                    Join Discord
                  </Button>
                </CardContent>
              </Card>
            )}

            <Card sx={{ background: theme.palette.background.paper }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Bug Reports & Features
                </Typography>
                <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                  Found a bug or have a feature request? Open an issue on GitHub.
                </Typography>
                <Button
                  variant="outlined"
                  component="a"
                  href="https://github.com/pretty-chitty/pretty-chitty/issues"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    borderColor: theme.palette.secondary.main,
                    color: theme.palette.text.primary,
                    "&:hover": {
                      borderColor: theme.palette.secondary.light,
                      background: "rgba(140, 0, 255, 0.1)",
                    },
                  }}
                >
                  Open an Issue
                </Button>
              </CardContent>
            </Card>
          </Stack>
        </Grid2>
      </Grid2>
    </Box>
  );
}
